import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';

const translations = {
  fr: { full: 'Complet', date: 'Date', place: 'Lieu', perTeam: 'par équipe', perPerson: 'par personne', teams: 'équipes inscrites', people: 'personnes inscrites', view: 'Visualiser le tournoi', participants: 'Visualiser les inscrits', closed: 'Inscriptions fermées', register: "S'inscrire", registerTeam: 'Inscrire une équipe', title: 'Team Building Padel', business: 'Padel Mendyx Entreprises', intro: 'Retrouvez l’événement organisé pour votre entreprise et inscrivez-vous directement.', loading: 'Chargement des événements...', upcoming: 'Team Building à venir', none: 'Aucun Team Building n’est actuellement ouvert aux inscriptions.', past: 'Anciens Team Building', pastIntro: 'Quelques événements déjà organisés par Padel Mendyx.', registered: 'Inscrits', close: 'Fermer', loadingPeople: 'Chargement des inscrits...', noTeam: 'Aucune équipe inscrite pour le moment.', team: 'Équipe {{number}}', unnamed: 'Équipe sans nom' },
  de: { full: 'Ausgebucht', date: 'Datum', place: 'Ort', perTeam: 'pro Team', perPerson: 'pro Person', teams: 'Teams angemeldet', people: 'Personen angemeldet', view: 'Turnier ansehen', participants: 'Teilnehmer ansehen', closed: 'Anmeldung geschlossen', register: 'Anmelden', registerTeam: 'Team anmelden', title: 'Padel-Teambuilding', business: 'Padel Mendyx für Unternehmen', intro: 'Finde das Event deines Unternehmens und melde dich direkt an.', loading: 'Events werden geladen...', upcoming: 'Kommende Teambuildings', none: 'Derzeit ist kein Teambuilding zur Anmeldung geöffnet.', past: 'Vergangene Teambuildings', pastIntro: 'Einige bereits organisierte Events.', registered: 'Angemeldet', close: 'Schließen', loadingPeople: 'Teilnehmer werden geladen...', noTeam: 'Noch kein Team angemeldet.', team: 'Team {{number}}', unnamed: 'Team ohne Namen' },
  nl: { full: 'Volzet', date: 'Datum', place: 'Locatie', perTeam: 'per team', perPerson: 'per persoon', teams: 'teams ingeschreven', people: 'personen ingeschreven', view: 'Toernooi bekijken', participants: 'Deelnemers bekijken', closed: 'Inschrijvingen gesloten', register: 'Inschrijven', registerTeam: 'Team inschrijven', title: 'Padel teambuilding', business: 'Padel Mendyx Vlaanderen voor bedrijven', intro: 'Vind het evenement van je bedrijf en schrijf je rechtstreeks in.', loading: 'Evenementen laden...', upcoming: 'Komende teambuildings', none: 'Momenteel is geen teambuilding geopend voor inschrijvingen.', past: 'Voorbije teambuildings', pastIntro: 'Enkele reeds georganiseerde evenementen.', registered: 'Ingeschreven', close: 'Sluiten', loadingPeople: 'Deelnemers laden...', noTeam: 'Nog geen team ingeschreven.', team: 'Team {{number}}', unnamed: 'Team zonder naam' },
  en: { full: 'Full', date: 'Date', place: 'Location', perTeam: 'per team', perPerson: 'per person', teams: 'teams registered', people: 'people registered', view: 'View tournament', participants: 'View participants', closed: 'Registration closed', register: 'Register', registerTeam: 'Register a team', title: 'Padel team building', business: 'Padel Mendyx for businesses', intro: 'Find your company event and register directly.', loading: 'Loading events...', upcoming: 'Upcoming team building', none: 'No team-building event is currently open.', past: 'Past team building', pastIntro: 'Some events already organised by Padel Mendyx.', registered: 'Registered', close: 'Close', loadingPeople: 'Loading participants...', noTeam: 'No teams registered yet.', team: 'Team {{number}}', unnamed: 'Unnamed team' },
};

const TeamBuildingList = () => {
  const { language } = useLanguage();
  const t = createTranslator(language, translations);
  const navigate = useNavigate();

  const [events, setEvents] = useState([]);
  const [participantsCounts, setParticipantsCounts] = useState({});
  const [individualParticipantsCounts, setIndividualParticipantsCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRegistrationsEvent, setSelectedRegistrationsEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [registrationsError, setRegistrationsError] = useState('');

  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        setError('');

        const snapshot = await getDocs(
          collection(db, 'team_building_catalog')
        );

        const loadedEvents = snapshot.docs
          .map((document) => {
            const data = document.data();

            return {
              ...data,
              registrationMode: data.registrationMode === 'individual' ? 'individual' : 'team',
              id: Number(data.id ?? document.id),
            };
          })
          .filter(
            (event) =>
              event.visible !== false &&
              event.archived !== true
          );

        setEvents(loadedEvents);
      } catch (loadError) {
        console.error(
          'Erreur de chargement des Team Building :',
          loadError
        );
        setError(
          'Impossible de charger les Team Building pour le moment.'
        );
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  useEffect(() => {
    fetch('https://us-central1-mendyx-adafe.cloudfunctions.net/protectedGetTeamBuildingParticipantsCounts')
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('COUNT_ERROR')))
      .then((data) => setIndividualParticipantsCounts(data.counts || {}))
      .catch((countError) => console.warn('Impossible de récupérer les inscriptions individuelles :', countError));
  }, []);

  useEffect(() => {
    const loadParticipantsCounts = async () => {
      try {
        const response = await fetch(
          'https://us-central1-mendyx-adafe.cloudfunctions.net/getAllParticipantsCounts'
        );

        const data = await response.json();

        if (!response.ok || !data.counts) {
          return;
        }

        const parsedCounts = {};

        Object.entries(data.counts).forEach(([id, count]) => {
          parsedCounts[Number(id)] = Number(count || 0);
        });

        setParticipantsCounts(parsedCounts);
      } catch (countError) {
        console.warn(
          'Impossible de récupérer le nombre d’inscriptions :',
          countError
        );
      }
    };

    loadParticipantsCounts();
  }, []);

  const { upcomingEvents, pastEvents } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const upcoming = [];
    const past = [];

    events.forEach((event) => {
      const eventDate = new Date(`${event.date}T00:00:00`);
      eventDate.setHours(0, 0, 0, 0);

      if (eventDate >= today) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });

    upcoming.sort(
      (a, b) =>
        new Date(`${a.date}T00:00:00`) -
        new Date(`${b.date}T00:00:00`)
    );

    past.sort(
      (a, b) =>
        new Date(`${b.date}T00:00:00`) -
        new Date(`${a.date}T00:00:00`)
    );

    return {
      upcomingEvents: upcoming,
      pastEvents: past.slice(0, 6),
    };
  }, [events]);

  const formatDate = (dateString) => {
    if (!dateString) return '';

    return new Date(`${dateString}T00:00:00`).toLocaleDateString(
      language,
      {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }
    );
  };

  const openRegistrationsModal = async (event) => {
    try {
      setSelectedRegistrationsEvent(event);
      setRegistrations([]);
      setRegistrationsError('');
      setRegistrationsLoading(true);

      const snapshot = await getDocs(
        collection(
          db,
          'tournament_registrations',
          String(event.id),
          'inscriptions'
        )
      );

      const loadedRegistrations = snapshot.docs
        .map((document) => ({
          id: document.id,
          ...document.data(),
        }))
        .sort((a, b) =>
          String(a.teamName || '').localeCompare(String(b.teamName || ''), 'fr')
        );

      setRegistrations(loadedRegistrations);
    } catch (modalError) {
      console.error('Erreur chargement inscrits Team Building:', modalError);
      setRegistrationsError('Impossible de charger les inscrits pour le moment.');
    } finally {
      setRegistrationsLoading(false);
    }
  };

  const closeRegistrationsModal = () => {
    setSelectedRegistrationsEvent(null);
    setRegistrations([]);
    setRegistrationsError('');
    setRegistrationsLoading(false);
  };

  const renderUpcomingCard = (event) => {
    const hasChoice = event.registrationMode !== 'individual' && event.initiationEnabled === true;
    const isIndividual = event.registrationMode === 'individual';
    const price = Number(event.price || 0);
    const currentCount = isIndividual
      ? individualParticipantsCounts[event.id] || 0
      : participantsCounts[event.id] || 0;
    const maximumTeams = Number(event.players || 0);
    const isFull =
      maximumTeams > 0 && currentCount >= maximumTeams && (!hasChoice || (Number(event.initiationCapacity) > 0 && (individualParticipantsCounts[event.id] || 0) >= Number(event.initiationCapacity)));

    return (
      <article
        key={event.id}
        className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-lg"
      >
        {event.picture ? (
          <div className="h-60 w-full bg-gray-100">
            <img
              src={event.picture}
              alt={event.name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="flex h-44 items-center justify-center bg-gradient-to-br from-[#4c2f8e] to-[#ea4c89] px-6 text-center">
            <span className="text-3xl font-black text-white">
              Team Building Padel
            </span>
          </div>
        )}

        <div className="p-6">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              {event.company && (
                <p className="mb-1 text-sm font-bold uppercase tracking-wider text-[#ea4c89]">
                  {event.company}
                </p>
              )}

              <h2 className="text-2xl font-black text-[#4c2f8e]">
                {event.name}
              </h2>
            </div>

            {isFull && (
              <span className="rounded-full bg-red-100 px-3 py-1 text-sm font-bold text-red-700">
                {t('full')}
              </span>
            )}
          </div>

          <div className="space-y-2 text-gray-700">
            <p>
              <strong>{t('date')} :</strong> {formatDate(event.date)}
              {event.time ? ` ${event.time}` : ''}
            </p>

            {event.location && (
              <p>
                <strong>{t('place')} :</strong> {event.location}
              </p>
            )}

            {event.description && (
              <p className="pt-2 leading-relaxed text-gray-600">
                {event.description}
              </p>
            )}
          </div>

          <div className="mt-6 border-t border-gray-100 pt-5">
            <div className="mb-4">
              {(price > 0 || hasChoice) && (
                <p className="font-bold text-gray-900">
                  {price.toLocaleString(language, { maximumFractionDigits: 2 })} €
                  <span className="font-normal text-gray-500">
                    {' '}{t('perPerson')}
                  </span>
                  {!isIndividual && <span className="block text-sm font-normal text-gray-500">2 × {price.toLocaleString(language)} € = {(price * 2).toLocaleString(language)} € {t('perTeam')}</span>}
                </p>
              )}

              {maximumTeams > 0 && (
                <p className="text-sm text-gray-500">
                  {currentCount * (isIndividual ? 1 : 2)} / {maximumTeams * (isIndividual ? 1 : 2)} {t('people')}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {!isIndividual && <button
              type="button"
              onClick={() => navigate(`/tournament/${event.id}/view`)}
              className="rounded-xl border-2 border-[#4c2f8e] bg-purple-50 px-5 py-3 text-center font-black text-[#4c2f8e] shadow-sm transition hover:bg-[#4c2f8e] hover:text-white hover:shadow-md sm:col-span-2"
            >
              {t('view')}
            </button>}

            {!isIndividual && <button
              type="button"
              onClick={() => openRegistrationsModal(event)}
              className="rounded-xl border-2 border-[#ea4c89] bg-white px-5 py-3 text-center font-black text-[#ea4c89] shadow-sm transition hover:bg-[#ea4c89] hover:text-white hover:shadow-md"
            >
              {t('participants')}
            </button>}

            <button
              type="button"
              onClick={() => navigate(isIndividual || hasChoice ? `/team-building/${event.id}/register` : `/register/${event.id}`)}
              disabled={isFull || event.registrationDisabled}
              className={`rounded-xl px-5 py-3 text-center font-black shadow-sm transition ${isIndividual ? 'sm:col-span-2' : ''} ${
                isFull || event.registrationDisabled
                  ? 'cursor-not-allowed bg-gray-300 text-gray-600'
                  : 'bg-[#4c2f8e] text-white hover:bg-[#3b2472] hover:shadow-md'
              }`}
            >
              {isFull
                ? t('full')
                : event.registrationDisabled
                ? t('closed')
                : t(isIndividual || hasChoice ? 'register' : 'registerTeam')}
            </button>
            </div>
          </div>
        </div>
      </article>
    );
  };

  const renderPastCard = (event) => (
    <article
      key={event.id}
      className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
    >
      {event.picture && (
        <div className="h-40 bg-gray-100">
          <img
            src={event.picture}
            alt={event.name}
            className="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      )}

      <div className="p-5">
        {event.company && (
          <p className="mb-1 text-xs font-bold uppercase tracking-wider text-[#ea4c89]">
            {event.company}
          </p>
        )}

        <h3 className="text-xl font-bold text-[#4c2f8e]">
          {event.name}
        </h3>

        <p className="mt-2 text-sm text-gray-500">
          {formatDate(event.date)}
        </p>
      </div>
    </article>
  );

  return (
    <>
      <Helmet>
        <title>{t('title')} | Padel Mendyx</title>
        <meta
          name="description"
          content="Inscrivez votre équipe à un Team Building de padel organisé par Padel Mendyx."
        />
        <meta
          name="robots"
          content="noindex, nofollow"
        />
      </Helmet>

      <main className="container mx-auto my-20 px-4">
        <header className="mx-auto mb-14 max-w-4xl text-center">
          <p className="mb-3 text-sm font-black uppercase tracking-[0.25em] text-[#ea4c89]">
            {t('business')}
          </p>

          <h1 className="text-4xl font-black text-[#4c2f8e] md:text-6xl">
            {t('title')}
          </h1>

          <p className="mx-auto mt-5 max-w-2xl text-lg leading-relaxed text-gray-600">
            {t('intro')}
          </p>
        </header>

        {loading && (
          <p className="text-center text-gray-500">
            {t('loading')}
          </p>
        )}

        {error && (
          <div className="mx-auto max-w-2xl rounded-xl border border-red-200 bg-red-50 p-4 text-center text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <section>
              <h2 className="mb-8 text-center text-3xl font-black text-[#4c2f8e]">
                {t('upcoming')}
              </h2>

              {upcomingEvents.length > 0 ? (
                <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 lg:grid-cols-2">
                  {upcomingEvents.map(renderUpcomingCard)}
                </div>
              ) : (
                <div className="mx-auto max-w-2xl rounded-2xl bg-gray-50 p-8 text-center text-gray-500">
                  {t('none')}
                </div>
              )}
            </section>

            {pastEvents.length > 0 && (
              <section className="mt-20 border-t border-gray-200 pt-14">
                <h2 className="mb-3 text-center text-3xl font-black text-[#4c2f8e]">
                  {t('past')}
                </h2>

                <p className="mb-8 text-center text-gray-500">
                  {t('pastIntro')}
                </p>

                <div className="mx-auto grid max-w-5xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  {pastEvents.map(renderPastCard)}
                </div>
              </section>
            )}
          </>
        )}
      </main>

      {selectedRegistrationsEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 p-5">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.2em] text-[#ea4c89]">
                  {t('registered')}
                </p>
                <h2 className="mt-1 text-2xl font-black text-[#4c2f8e]">
                  {selectedRegistrationsEvent.name}
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  {registrations.length * 2} / {(selectedRegistrationsEvent.players || 0) * 2} {t('people')}
                </p>
              </div>

              <button
                type="button"
                onClick={closeRegistrationsModal}
                className="rounded-full bg-gray-100 px-4 py-2 font-bold text-gray-700 hover:bg-gray-200"
              >
                {t('close')}
              </button>
            </div>

            <div className="max-h-[60vh] overflow-y-auto p-5">
              {registrationsLoading && (
                <p className="text-center text-gray-500">{t('loadingPeople')}</p>
              )}

              {registrationsError && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-center text-red-700">
                  {registrationsError}
                </div>
              )}

              {!registrationsLoading && !registrationsError && registrations.length === 0 && (
                <div className="rounded-xl bg-gray-50 p-6 text-center text-gray-500">
                  {t('noTeam')}
                </div>
              )}

              {!registrationsLoading && !registrationsError && registrations.length > 0 && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {registrations.map((registration, index) => (
                    <div
                      key={registration.id}
                      className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                    >
                      <p className="text-xs font-bold uppercase text-gray-400">
                        {t('team', { number: index + 1 })}
                      </p>
                      <p className="mt-1 text-lg font-black text-[#4c2f8e]">
                        {registration.teamName || t('unnamed')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TeamBuildingList;
